import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import logo from "../assets/cograd-blue-logo.webp";
import medhaIcon from "../assets/medha-icon.png";
import "./Header.scss";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown } from "lucide-react";

// ─── Nav config ───────────────────────────────────────────────────────────────

const NAV = [
  { label: "Home",        href: "/"          },
  { label: "About Us",    href: "/about-us"  },
  { label: "Services",    href: "/services"  },
  {
    label: "Programs",
    dropdown: [
      { label: "YUVASETU Hackathon", href: "/hackathon",                  desc: "National School AI Hackathon 2026" },
      { label: "Fellowship",    href: "/fellowship",                       desc: "Leadership & educator training"  },
      { label: "NCF Trainings", href: "/ncf-training",                     desc: "NEP 2020 aligned curriculum"     },
      { label: "Vidya Voice",   href: "https://vidyavoice.cograd.in",      desc: "Student voice platform", external: true },
    ],
  },
  { label: "NextGen School", href: "/nextgen-school" },
  { label: "Careers",     href: "/careers"   },
  { label: "Incubation",  href: "/incubation"},
];

// ─── Header ───────────────────────────────────────────────────────────────────

// `autoHide`: slide the header away while scrolling down and bring it back on
// scroll up. Pages can also hold it hidden briefly (e.g. while jumping to a
// section) by dispatching `new CustomEvent("hdr:hide", { detail: ms })`.
export default function Header({ autoHide = false }) {
  const [scrolled,     setScrolled]     = useState(false);
  const [mobileOpen,   setMobileOpen]   = useState(false);
  const [hoveredNav,   setHoveredNav]   = useState(null);
  const [programsOpen, setProgramsOpen] = useState(false);
  const [hidden,       setHidden]       = useState(false);
  const { pathname } = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    if (!autoHide) return undefined;
    let lastY = window.scrollY;
    let holdUntil = 0;
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - lastY;
      if (y < 120) setHidden(false);
      else if (Date.now() < holdUntil) setHidden(true);
      else if (dy > 6) setHidden(true);
      else if (dy < -6) setHidden(false);
      if (Math.abs(dy) > 6 || y < 120) lastY = y;
    };
    const onHold = (e) => { holdUntil = Date.now() + (Number(e.detail) || 900); setHidden(true); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("hdr:hide", onHold);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("hdr:hide", onHold);
    };
  }, [autoHide]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const openPrograms  = () => { clearTimeout(closeTimer.current); setProgramsOpen(true);  };
  const closePrograms = () => { closeTimer.current = setTimeout(() => setProgramsOpen(false), 120); };

  const isActive = (item) =>
    item.href
      ? pathname === item.href
      : item.dropdown?.some(s => pathname === s.href);

  return (
    <>
      {/* ─── Main header ──────────────────────────────────────────────── */}
      <header className={`hdr${scrolled ? " hdr--up" : ""}${hidden && !mobileOpen && !programsOpen ? " hdr--hidden" : ""}`}>
        <span className="hdr__border" />

        <div className="hdr__wrap">

          {/* Logo */}
          <a href="/" className="hdr__logo">
            <img src={logo} alt="CoGrad" />
          </a>

          {/* Desktop nav */}
          <nav className="hdr__nav" onMouseLeave={() => setHoveredNav(null)}>
            <div className="hdr__nav-pill">
              {NAV.map((item) => {
                if (item.dropdown) {
                  return (
                    <div
                      key={item.label}
                      className="hdr__drop-wrap"
                      onMouseEnter={openPrograms}
                      onMouseLeave={closePrograms}
                    >
                      <button
                        className={`hdr__link hdr__drop-btn${isActive(item) ? " hdr__link--on" : ""}`}
                      >
                        <span className="hdr__link-txt">{item.label}</span>
                        <ChevronDown
                          size={13}
                          className={`hdr__chevron${programsOpen ? " hdr__chevron--up" : ""}`}
                        />
                      </button>

                      <div
                        className={`hdr__dropdown${programsOpen ? " hdr__dropdown--open" : ""}`}
                        onMouseEnter={openPrograms}
                        onMouseLeave={closePrograms}
                      >
                        {item.dropdown.map(sub => (
                          <a
                            key={sub.href}
                            href={sub.href}
                            target={sub.external ? "_blank" : undefined}
                            rel={sub.external ? "noopener noreferrer" : undefined}
                            className={`hdr__drop-item${pathname === sub.href ? " hdr__drop-item--on" : ""}`}
                          >
                            <span className="hdr__drop-label">{sub.label}</span>
                            <span className="hdr__drop-desc">{sub.desc}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  );
                }

                return (
                  <a
                    key={item.label}
                    href={item.href}
                    target={item.external ? "_blank" : undefined}
                    rel={item.external ? "noopener noreferrer" : undefined}
                    className={`hdr__link${isActive(item) ? " hdr__link--on" : ""}`}
                    onMouseEnter={() => setHoveredNav(item.label)}
                    onMouseLeave={() => setHoveredNav(null)}
                  >
                    {hoveredNav === item.label && (
                      <motion.span className="hdr__pill" layoutId="nav-pill"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.32 }} />
                    )}
                    <span className="hdr__link-txt">{item.label}</span>
                  </a>
                );
              })}
            </div>
          </nav>

          {/* Right actions */}
          <div className="hdr__ctas">
            <a href="/hackathon" className="hdr-btn hdr-btn--fellowship">
              Hackathon
            </a>
            <a href="https://medha.cograd.in/" target="_blank" rel="noopener noreferrer" className="hdr-btn hdr-btn--medha">
              <img src={medhaIcon} alt="" className="hdr-btn__medha-icon" />
              Medha AI
            </a>
            <button className="hdr__burger" onClick={() => setMobileOpen(true)} aria-label="Menu">
              <span /><span /><span />
            </button>
          </div>

        </div>
      </header>

      {/* ─── Mobile drawer ────────────────────────────────────────────── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div className="drw-bg"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileOpen(false)} />

            <motion.aside className="drw"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 280 }}>

              <div className="drw__head">
                <a href="/" onClick={() => setMobileOpen(false)}>
                  <img src={logo} alt="CoGrad" className="drw__logo" />
                </a>
                <button className="drw__close" onClick={() => setMobileOpen(false)} aria-label="Close">
                  <X size={19} />
                </button>
              </div>

              <div className="drw__badge">🦈 As seen on <strong>Shark Tank India</strong></div>

              <nav className="drw__nav">
                {NAV.map((item, i) => {
                  if (item.dropdown) {
                    return (
                      <div key={item.label} className="drw__group">
                        <span className="drw__group-label">{item.label}</span>
                        {item.dropdown.map((sub, j) => (
                          <motion.div key={sub.label}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.04 + (i + j) * 0.04 }}>
                            <a
                              href={sub.href}
                              className={`drw__link drw__link--sub${pathname === sub.href ? " drw__link--on" : ""}`}
                              onClick={() => setMobileOpen(false)}
                            >
                              {sub.label}
                            </a>
                          </motion.div>
                        ))}
                      </div>
                    );
                  }

                  return (
                    <motion.div key={item.label}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.04 + i * 0.04 }}>
                      <a
                        href={item.href}
                        target={item.external ? "_blank" : undefined}
                        rel={item.external ? "noopener noreferrer" : undefined}
                        className={`drw__link${isActive(item) ? " drw__link--on" : ""}`}
                        onClick={() => setMobileOpen(false)}
                      >
                        {item.label}
                      </a>
                    </motion.div>
                  );
                })}
              </nav>

              <div className="drw__ctas">
                <a href="/hackathon" className="hdr-btn hdr-btn--fellowship w-full" onClick={() => setMobileOpen(false)}>
                  Hackathon
                </a>
                <a href="https://medha.cograd.in/" target="_blank" rel="noopener noreferrer" className="hdr-btn hdr-btn--medha w-full" onClick={() => setMobileOpen(false)}>
                  <img src={medhaIcon} alt="" className="hdr-btn__medha-icon" />
                  Medha AI
                </a>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
